/**
 * IMS URL Service for managing image provider URLs.
 */

interface ImageParams {
  width?: number
  height?: number
  quality?: number
  fit?: string
  auto?: string
}

class IMSUrlService {
  private static readonly PROVIDER_URLS: Record<string, string> = {
    IMGIX: 'https://ims.imgix.net',
    LAHAUS: 'https://media.lahaus.com'
  }

  /**
   * Get the base URL for the configured IMS provider.
   *
   * @returns The base URL for the configured provider.
   * @throws Error if the configured provider is not supported.
   */
  static getBaseUrl (): string {
    const config = useRuntimeConfig()
    const provider = config.public.imsImageProvider as string

    if (!provider || !this.PROVIDER_URLS[provider]) {
      throw new Error(
        `Unsupported IMS provider: ${provider}. Supported providers: ${Object.keys(
          this.PROVIDER_URLS
        ).join(', ')}`
      )
    }

    return this.PROVIDER_URLS[provider]
  }

  /**
   * Get the URL path and query parameters for image optimization based on the configured provider.
   *
   * @param width - Optional width for the image.
   * @param height - Optional height for the image.
   * @param quality - Optional quality (1-100). Higher values mean better quality.
   * @param fit - Optional fit mode.
   *              - IMGIX: 'crop', 'fill', 'clip', 'scale', 'max'
   *              - LAHAUS: Defaults to 'inside' if not specified
   * @param auto - Optional comma-separated auto parameters (e.g., 'compress,enhance,format').
   *              Only applies to IMGIX provider.
   * @returns Path fragment and query parameters string for image optimization.
   */
  static getImageParams ({
    width,
    height,
    quality,
    fit,
    auto
  }: ImageParams = {}): string {
    try {
      const config = useRuntimeConfig()
      const provider = config.public.imsImageProvider as string
      if (provider === 'LAHAUS') {
        return this._getLahausParams(width, height, quality, fit)
      } else {
        return this._getImgixParams(width, height, quality, fit, auto)
      }
    } catch (error) {
      // Fallback to IMGIX if useRuntimeConfig is not available (e.g., in non-Nuxt context)
      return this._getImgixParams(width, height, quality, fit, auto)
    }
  }

  /**
   * Build query parameters for IMGIX provider.
   */
  private static _getImgixParams (
    width?: number,
    height?: number,
    quality?: number,
    fit?: string,
    auto?: string
  ): string {
    const params: string[] = []

    // Default auto parameter for IMGIX
    const autoParam = auto || 'compress,enhance,format'
    params.push(`auto=${autoParam}`)

    if (width) {
      params.push(`w=${width}`)
    }
    if (height) {
      params.push(`h=${height}`)
    }

    if (quality !== undefined) {
      params.push(`q=${quality}`)
    }

    if (fit) {
      params.push(`fit=${fit}`)
    }

    return params.length > 0 ? `?${params.join('&')}` : ''
  }

  /**
   * Build path fragment and query parameters for LAHAUS provider.
   */
  private static _getLahausParams (
    width?: number,
    height?: number,
    quality?: number,
    fit?: string
  ): string {
    const params: string[] = []

    if (quality !== undefined) {
      params.push(`quality=${quality}`)
    }
    if (width) {
      params.push(`resize.width=${width}`)
    }
    if (height) {
      params.push(`resize.height=${height}`)
    }

    let fitValue = fit || 'inside'
    if (fit === 'crop') {
      fitValue = 'outside'
    }
    params.push(`fit=${fitValue}`)

    const queryString = params.length > 0 ? `?${params.join('&')}` : ''
    return queryString
  }
}

export default IMSUrlService
